import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { MfaEnrollmentService } from 'app/core/auth/mfa-enrollment/mfa-enrollment.service';
import { StepUpService } from 'app/core/auth/step-up/step-up.service';
import { STEP_UP_ACTION, STEP_UP_RETRIED } from 'app/core/auth/step-up/step-up.context';
import { ToastService } from 'app/shared/components/toast/toast.service';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from 'app/environments/environment';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

/**
 * Auth interceptor.
 *
 * Previously this stripped the Authorization header whenever the token looked
 * expired client-side, which guaranteed a 401 and an immediate signOut+reload —
 * the literal cause of LION-480 ("save -> suddenly logged out").
 *
 * Now it attaches the token to OUR API requests and, on a 401 from a non-auth
 * API endpoint, attempts a single refresh and retries the original request
 * before giving up. Proactive renewal lives in SessionService; this is the
 * safety net.
 *
 * LION-520 — Step-up MFA reactivo. Además del 401-refresh, sobre un 403 con
 * `error.error.code` el interceptor resuelve los códigos de bloqueo del backend:
 *   - MFA_STEPUP_REQUIRED → abre el step-up (un solo modal serializado) y
 *     reintenta la request original UNA vez con el token fresco.
 *   - MFA_ENROLLMENT_REQUIRED → toast + abre el enrolamiento (un solo modal
 *     serializado) y reintenta la request original UNA vez. Antes navegaba a
 *     /mfa-setup, lo que destruía el componente con el formulario a medio
 *     llenar y descartaba la request; ahora la pantalla queda en pie.
 *   - FORCE_PASSWORD_RESET_REQUIRED → toast con el mensaje del backend, propaga
 *     SIN navegar (ver nota más abajo).
 */
export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  // Capturamos el Injector para resolver de forma LAZY los servicios que sólo
  // usa el bloque 403 (StepUpService, ToastService, TranslocoService). Así el
  // path del 401 —y sus tests existentes— no arrastran esas dependencias.
  const injector = inject(Injector);

  // Only attach the token to OUR API. Prevents leaking the Bearer to any
  // future external HttpClient call (S3, maps, analytics, i18n assets, ...).
  const isApiRequest = req.url.startsWith(environment.apiURL);

  const token = authService.accessToken;
  const authReq =
    token && isApiRequest
      ? req.clone({
          headers: req.headers.set('Authorization', 'Bearer ' + token)
        })
      : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      const isAuthEndpoint = req.url.startsWith(`${environment.apiURL}auth/`);

      // --- 401: refresh-on-401 (sin cambios respecto a LION-480) ------------
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        isApiRequest &&
        !isAuthEndpoint &&
        // Sin token no había sesión que refrescar: un request anónimo a un
        // endpoint protegido (p. ej. EventWorkflowService.preload() en el
        // APP_INITIALIZER, que corre en TODA carga de página, incluida
        // /reset-password) no debe disparar refresh-y-signOut espurio.
        token
      ) {
        // Try one refresh, then replay the original request with the new token.
        return authService.refreshToken().pipe(
          switchMap(refreshed => {
            if (refreshed) {
              const retried = req.clone({
                headers: req.headers.set(
                  'Authorization',
                  'Bearer ' + authService.accessToken
                )
              });
              return next(retried);
            }

            // Refresh failed -> graceful sign out (no abrupt location.reload()).
            const redirectURL = router.url;
            authService.signOut().subscribe();
            router.navigate(['sign-in'], { queryParams: { redirectURL } });

            return throwError(() => error);
          })
        );
      }

      // --- 403: códigos de bloqueo del backend (LION-520) -------------------
      if (error instanceof HttpErrorResponse && error.status === 403 && isApiRequest) {
        const code = (error.error as { code?: string } | null)?.code;

        // Anti-recursión de AMBOS flujos de MFA: los endpoints que emiten los
        // propios modales —auth/mfa/step-up y auth/mfa/enroll/{start,confirm}—
        // más los de sesión (auth/refresh, auth/sign-in, auth/logout) NUNCA
        // deben re-disparar step-up ni enrolamiento sobre sí mismos. Sin la
        // entrada auth/mfa/enroll habría espera circular: ensureEnrollment()
        // sólo libera su observable en vuelo cuando el diálogo cierra, y el
        // diálogo cierra cuando enroll/confirm resuelve.
        // OJO: auth/admin/reset-password ES un /auth/* pero SÍ debe entrar al
        // handler (es quien EMITE el 403 MFA_STEPUP_REQUIRED), por eso NO
        // usamos el `!isAuthEndpoint` genérico del 401: excluimos sólo la lista
        // puntual de endpoints de MFA y de sesión.
        const isMfaOrSessionEndpoint =
          req.url.startsWith(`${environment.apiURL}auth/mfa/step-up`) ||
          req.url.startsWith(`${environment.apiURL}auth/mfa/enroll`) ||
          req.url.startsWith(`${environment.apiURL}auth/refresh`) ||
          req.url.startsWith(`${environment.apiURL}auth/sign-in`) ||
          req.url.startsWith(`${environment.apiURL}auth/logout`);

        switch (code) {
          case 'MFA_STEPUP_REQUIRED': {
            // Anti-bucle determinístico: si esta request YA es el reintento, el
            // step-up no alcanzó. Propagamos SIN reabrir el modal (una sola vez).
            const alreadyRetried = req.context.get(STEP_UP_RETRIED);

            if (isMfaOrSessionEndpoint || alreadyRetried) {
              return throwError(() => error);
            }

            // Resolución LAZY (sólo este branch la necesita): mantiene el path
            // del 401 y de los 403 genéricos libres de esta dependencia.
            const stepUpService = injector.get(StepUpService);

            // Etiqueta legible de la acción, publicada por el call-site en el
            // HttpContext (la única capa que sabe QUÉ se estaba ejecutando). El
            // interceptor sólo la reenvía al modal; es UI, nunca un id.
            const actionLabel = req.context.get(STEP_UP_ACTION);

            // ensureStepUp() está serializado (un solo modal aunque haya N 403
            // concurrentes). Al resolver true, leemos el accessToken FRESCO
            // DENTRO del switchMap (lo persistió stepUpMfa) y reintentamos
            // marcando el centinela para que un 2º 403 no vuelva a entrar acá.
            // Si ya había un step-up en vuelo, el modal se reusa con la etiqueta
            // de la PRIMERA request: se confirma UN segundo factor, no N acciones.
            return stepUpService.ensureStepUp(actionLabel).pipe(
              switchMap(verified => {
                if (!verified) {
                  return throwError(() => error);
                }

                const retried = req.clone({
                  headers: req.headers.set(
                    'Authorization',
                    'Bearer ' + authService.accessToken
                  ),
                  context: req.context.set(STEP_UP_RETRIED, true)
                });
                return next(retried);
              })
            );
          }

          case 'MFA_ENROLLMENT_REQUIRED': {
            // Mismas guardas que el step-up: los endpoints de MFA/sesión no
            // deben abrir el modal sobre sí mismos, y el centinela garantiza un
            // solo reintento por request. Reusamos STEP_UP_RETRIED a propósito:
            // el objetivo ("esta request ya se reintentó una vez tras resolver
            // un bloqueo de MFA") es idéntico, y un token propio permitiría un
            // segundo reintento encadenado entre ambos códigos.
            const alreadyRetried = req.context.get(STEP_UP_RETRIED);

            if (isMfaOrSessionEndpoint || alreadyRetried) {
              return throwError(() => error);
            }

            // El toast se muestra ANTES de abrir el modal: es lo que le explica
            // al usuario por qué le aparece el enrolamiento sin haberlo pedido.
            injector
              .get(ToastService)
              .showMessage(
                '',
                injector.get(TranslocoService).translate('mfaErrors.enrollmentRequired'),
                'warning'
              );

            // Resolución LAZY, igual que StepUpService.
            const enrollmentService = injector.get(MfaEnrollmentService);

            // ensureEnrollment() está serializado (un solo modal aunque haya N
            // 403 concurrentes). Al resolver true, el enroll/confirm ya persistió
            // el accessToken reemitido: lo leemos FRESCO dentro del switchMap y
            // reintentamos la request original con el body intacto.
            return enrollmentService.ensureEnrollment().pipe(
              switchMap(enrolled => {
                if (!enrolled) {
                  return throwError(() => error);
                }

                const retried = req.clone({
                  headers: req.headers.set(
                    'Authorization',
                    'Bearer ' + authService.accessToken
                  ),
                  context: req.context.set(STEP_UP_RETRIED, true)
                });
                return next(retried);
              })
            );
          }

          case 'FORCE_PASSWORD_RESET_REQUIRED': {
            // Caso DEFENSIVO: el titular del admin-reset entra por el link del
            // email, no por la app — un ADMIN logueado no debería recibir este
            // código en condiciones normales. NO navegamos a /reset-password:
            // está bajo NoAuthGuard (rebota al usuario logueado) y exige ?token=
            // (pantalla muerta sin token). Sólo mostramos el mensaje del backend
            // y propagamos, sacando al usuario del bloqueo con un aviso claro y
            // sin loops de redirect.
            injector
              .get(ToastService)
              .showMessage(
                '',
                error.error?.message ??
                  injector.get(TranslocoService).translate('mfaErrors.forcePasswordReset'),
                'warning'
              );
            return throwError(() => error);
          }

          default:
            return throwError(() => error);
        }
      }

      return throwError(() => error);
    })
  );
};
